/**
 * Custom hook pour la gestion des factures
 * ✅ SKILL FACTURXPRO: Architecture 3 couches
 *
 * Component (InvoicesListResponsive.tsx)
 *    ↓
 * Custom Hook (useInvoicesManagement) ← VOUS ÊTES ICI
 *    ↓
 * API Layer (invoicesAPI, remindersAPI)
 *    ↓
 * Backend
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { invoicesAPI, remindersAPI } from '../utils/api';
import { Invoice as BaseInvoice } from '../types/models';
import { notify } from '../stores/notificationStore';
import { logger } from '../utils/logger';

// Extended Invoice type with relations (as returned by API with include)
interface Invoice extends BaseInvoice {
  Client?: {
    fullName?: string;
    companyName?: string;
    email?: string;
  };
}

interface UseInvoicesManagementReturn {
  // Data
  invoices: Invoice[];
  filteredInvoices: Invoice[];

  // UI State
  loading: boolean;
  error: string | null;
  activeMenu: string | null;

  // Filter State
  searchQuery: string;
  activeStatuses: string[];
  dateFrom: string;
  dateTo: string;

  // Actions - CRUD
  fetchInvoices: () => Promise<void>;
  createInvoice: (data: Partial<Invoice>) => Promise<Invoice | null>;
  updateInvoice: (id: string, data: any) => Promise<void>;
  deleteInvoice: (id: string) => Promise<void>;

  // Actions - Status & Payment
  updateInvoiceStatus: (id: string, newStatus: string) => Promise<void>;
  markInvoiceAsPaid: (id: string, amount: number) => Promise<void>;

  // Actions - Related
  sendReminderForInvoice: (invoiceId: string | number) => Promise<void>;

  // Actions - UI
  setActiveMenu: (id: string | null) => void;
  setSearchQuery: (query: string) => void;
  setActiveStatuses: (statuses: string[]) => void;
  setDateFrom: (date: string) => void;
  setDateTo: (date: string) => void;
  clearError: () => void;

  // Computed
  getStatusConfig: (status: string) => { label: string; variant: string; icon: any };
  isOverdue: (invoice: Invoice) => boolean;
  formatDate: (dateStr: string) => string;
  formatCurrency: (amount: number) => string;
}

export function useInvoicesManagement(): UseInvoicesManagementReturn {
  // ==================== STATE ====================

  // Data state
  const [invoices, setInvoices] = useState<Invoice[]>([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [activeStatuses, setActiveStatuses] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // ==================== DATA LOADING ====================

  /**
   * Charger les factures
   */
  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await invoicesAPI.getAll();
      setInvoices(Array.isArray(data) ? data : []);
    } catch (err: any) {
      const errorMessage = err.message || 'Erreur lors du chargement des factures';
      setError(errorMessage);
      logger.error('fetchInvoices error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // ==================== CRUD OPERATIONS ====================

  /**
   * Créer une facture
   */
  const createInvoice = useCallback(async (data: Partial<Invoice>) => {
    try {
      setLoading(true);
      const newInvoice = await invoicesAPI.create(data);
      notify.success('Facture créée avec succès', 'Succès');
      await fetchInvoices();
      return newInvoice;
    } catch (err: any) {
      logger.error('createInvoice error:', err);
      notify.error(err.message || 'Erreur lors de la création de la facture', 'Erreur');
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchInvoices]);

  /**
   * Mettre à jour une facture
   */
  const updateInvoice = useCallback(async (id: string, data: any) => {
    try {
      setLoading(true);
      await invoicesAPI.update(id, data);
      notify.success('Facture mise à jour avec succès', 'Succès');
      await fetchInvoices();
    } catch (err: any) {
      logger.error('updateInvoice error:', err);
      notify.error(err.message || 'Erreur lors de la mise à jour de la facture', 'Erreur');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchInvoices]);

  /**
   * Supprimer une facture
   */
  const deleteInvoice = useCallback(async (id: string) => {
    try {
      setLoading(true);
      await invoicesAPI.delete(id);
      notify.success('Facture supprimée avec succès', 'Suppression réussie');
      await fetchInvoices();
    } catch (err: any) {
      logger.error('deleteInvoice error:', err);
      notify.error(err.message || 'Erreur lors de la suppression', 'Erreur');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchInvoices]);

  // ==================== STATUS & PAYMENT MANAGEMENT ====================

  /**
   * Mettre à jour le statut d'une facture
   */
  const updateInvoiceStatus = useCallback(async (id: string, newStatus: string) => {
    try {
      setLoading(true);
      await invoicesAPI.updateStatus(id, newStatus);
      notify.success('Statut mis à jour', 'Mise à jour réussie');
      await fetchInvoices();
    } catch (err: any) {
      logger.error('updateInvoiceStatus error:', err);
      notify.error(err.message || 'Erreur lors de la mise à jour', 'Erreur');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchInvoices]);

  /**
   * Marquer une facture comme payée
   */
  const markInvoiceAsPaid = useCallback(async (id: string, amount: number) => {
    try {
      setLoading(true);
      await invoicesAPI.markAsPaid(id, amount);
      notify.success('Facture marquée comme payée !', 'Mise à jour réussie');
      await fetchInvoices();
    } catch (err: any) {
      logger.error('markInvoiceAsPaid error:', err);
      notify.error(err.message || 'Erreur lors de la mise à jour', 'Erreur');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchInvoices]);

  // ==================== RELATED OPERATIONS ====================

  /**
   * Envoyer une relance pour une facture
   */
  const sendReminderForInvoice = useCallback(async (invoiceId: string | number) => {
    try {
      const id = typeof invoiceId === 'string' ? parseInt(invoiceId, 10) : invoiceId;
      await remindersAPI.sendManualInvoice(id);
      notify.success('Relance envoyée avec succès', 'Email envoyé');
    } catch (err: any) {
      logger.error('sendReminderForInvoice error:', err);
      notify.error(err.message || 'Erreur lors de l\'envoi de la relance', 'Erreur d\'envoi');
      throw err;
    }
  }, []);

  // ==================== HELPER FUNCTIONS ====================

  /**
   * Obtenir la configuration du statut (label, variant, icon)
   */
  const getStatusConfig = useCallback((status: string) => {
    const configs: Record<string, { label: string; variant: string; icon: any }> = {
      DRAFT: { label: 'Brouillon', variant: 'pending', icon: null },
      SENT: { label: 'Envoyée', variant: 'info', icon: null },
      PAID: { label: 'Payée', variant: 'success', icon: null },
      OVERDUE: { label: 'En retard', variant: 'alert', icon: null },
      CANCELED: { label: 'Annulée', variant: 'pending', icon: null },
      PARTIALLY_PAID: { label: 'Partiellement payée', variant: 'warning', icon: null },
    };
    return configs[status as keyof typeof configs] || configs.DRAFT;
  }, []);

  /**
   * Vérifier si une facture est en retard
   */
  const isOverdue = useCallback((invoice: Invoice) => {
    if (invoice.status === 'PAID' || !invoice.dueDate) return false;
    return new Date(invoice.dueDate) < new Date();
  }, []);

  /**
   * Formater une date en français
   */
  const formatDate = useCallback((dateStr: string) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return new Intl.DateTimeFormat('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }).format(date);
    } catch {
      return '-';
    }
  }, []);

  /**
   * Formater un montant en euros
   */
  const formatCurrency = useCallback((amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount || 0);
  }, []);

  /**
   * Nettoyer l'erreur
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // ==================== FILTERING ====================

  /**
   * Filtrer les factures selon les critères
   */
  const filteredInvoices = useMemo(() => {
    return invoices.filter((invoice) => {
      // Filtre par recherche (numéro, client, montant)
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesNumber = invoice.number?.toLowerCase().includes(query);
        const matchesClient =
          invoice.Client?.fullName?.toLowerCase().includes(query) ||
          invoice.Client?.companyName?.toLowerCase().includes(query);
        const matchesAmount = invoice.totalTTC?.toString().includes(query);

        if (!matchesNumber && !matchesClient && !matchesAmount) {
          return false;
        }
      }

      // Filtre par statuts actifs
      if (activeStatuses.length > 0) {
        if (!activeStatuses.includes(invoice.status.toLowerCase())) {
          return false;
        }
      }

      // Filtre par date (depuis)
      if (dateFrom) {
        const invoiceDate = new Date(invoice.issueDate);
        const fromDate = new Date(dateFrom);
        if (invoiceDate < fromDate) {
          return false;
        }
      }

      // Filtre par date (jusqu'à)
      if (dateTo) {
        const invoiceDate = new Date(invoice.issueDate);
        const toDate = new Date(dateTo);
        if (invoiceDate > toDate) {
          return false;
        }
      }

      return true;
    });
  }, [invoices, searchQuery, activeStatuses, dateFrom, dateTo]);

  // ==================== EFFECTS ====================

  // Charger les factures au montage
  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // ==================== RETURN ====================

  return {
    // Data
    invoices,
    filteredInvoices,

    // UI State
    loading,
    error,
    activeMenu,

    // Filter State
    searchQuery,
    activeStatuses,
    dateFrom,
    dateTo,

    // Actions - CRUD
    fetchInvoices,
    createInvoice,
    updateInvoice,
    deleteInvoice,

    // Actions - Status & Payment
    updateInvoiceStatus,
    markInvoiceAsPaid,

    // Actions - Related
    sendReminderForInvoice,

    // Actions - UI
    setActiveMenu,
    setSearchQuery,
    setActiveStatuses,
    setDateFrom,
    setDateTo,
    clearError,

    // Computed
    getStatusConfig,
    isOverdue,
    formatDate,
    formatCurrency,
  };
}
